import { useCallback, useEffect } from "react";
import dayjs from "dayjs";
import {
  FormControl,
  InputLabel,
  NativeSelect,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useIsTab } from "hooks/is-tab.hook";

const EBSCO_TIMEFRAME_KEYS = [
  "all-time",
  "past-year",
  "past-two-years",
  "past-three-years",
  "custom-date",
];

const formatMonthDate = (month, day) => {
  const date = dayjs(`${month}-01`).startOf("month");
  return date.date(Math.min(day, date.daysInMonth())).format("YYYY-MM-DD");
};

export default function EbscoTimeframe({
  timeframeValue,
  setTimeframeValue,
  startDateValue,
  setStartDateValue,
  endDateValue,
  setEndDateValue,
  frequencies,
  disabled = false,
  sx,
}) {
  const isTab = useIsTab();
  const options = frequencies.filter((frequency) =>
    EBSCO_TIMEFRAME_KEYS.includes(frequency.key)
  );
  const startMonth = startDateValue
    ? dayjs(startDateValue).format("YYYY-MM")
    : "";
  const endMonth = endDateValue
    ? dayjs(endDateValue).format("YYYY-MM")
    : "";

  const setMonthRange = useCallback((fromMonth, toMonth) => {
    setStartDateValue(formatMonthDate(fromMonth, 1));
    setEndDateValue(formatMonthDate(toMonth, 30));
  }, [setStartDateValue, setEndDateValue]);

  useEffect(() => {
    if (!EBSCO_TIMEFRAME_KEYS.includes(timeframeValue)) {
      setTimeframeValue("past-year");
      return;
    }
    if (timeframeValue === "all-time") return;
    if (timeframeValue !== "custom-date") return;

    const defaultStartMonth = dayjs().subtract(1, "month").format("YYYY-MM");
    const defaultEndMonth = dayjs().format("YYYY-MM");
    const fromMonth = startMonth || defaultStartMonth;
    const toMonth = endMonth || defaultEndMonth;
    const expectedStart = formatMonthDate(fromMonth, 1);
    const expectedEnd = formatMonthDate(toMonth, 30);

    if (startDateValue !== expectedStart || endDateValue !== expectedEnd) {
      setMonthRange(fromMonth, toMonth);
    }
  }, [
    timeframeValue,
    startMonth,
    endMonth,
    startDateValue,
    endDateValue,
    setMonthRange,
    setTimeframeValue,
  ]);

  const handleStartMonthChange = (month) => {
    if (!month?.isValid()) return;
    const nextStartMonth = month.format("YYYY-MM");
    const nextEndMonth = endMonth && endMonth >= nextStartMonth
      ? endMonth
      : nextStartMonth;
    setMonthRange(nextStartMonth, nextEndMonth);
  };

  const handleEndMonthChange = (month) => {
    if (!month?.isValid()) return;
    const nextEndMonth = month.format("YYYY-MM");
    const nextStartMonth = startMonth && startMonth <= nextEndMonth
      ? startMonth
      : nextEndMonth;
    setMonthRange(nextStartMonth, nextEndMonth);
  };

  const pickerDate = (month) => month ? dayjs(`${month}-01`) : null;

  return (
    <Stack
      direction={isTab ? "row" : "column"}
      alignItems="stretch"
      mt={1}
      spacing={1}
      sx={sx}
      onKeyUp={(event) => event.stopPropagation()}
    >
      <FormControl fullWidth={!isTab} disabled={disabled}>
        <InputLabel htmlFor="ebsco-timeframe" size="small">
          Timeframe
        </InputLabel>
        <NativeSelect
          size="small"
          input={<OutlinedInput />}
          value={timeframeValue}
          onChange={(event) => setTimeframeValue(event.target.value)}
          id="ebsco-timeframe"
          label="Timeframe"
        >
          {options.map((frequency) => (
            <option key={frequency.key} value={frequency.key}>
              {frequency.key === "custom-date"
                ? "Custom"
                : frequency.key === "all-time"
                  ? "All Time"
                  : frequency.label}
            </option>
          ))}
        </NativeSelect>
      </FormControl>

      {timeframeValue === "custom-date" && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack direction={isTab ? "row" : "column"} spacing={1}>
            <DatePicker
              label="Start Date"
              views={["month", "year"]}
              openTo="month"
              format="MMMM YYYY"
              value={pickerDate(startMonth)}
              maxDate={pickerDate(endMonth)}
              disabled={disabled}
              onChange={handleStartMonthChange}
              slotProps={{ textField: { fullWidth: !isTab, size: "small" } }}
            />
            <DatePicker
              label="End Date"
              views={["month", "year"]}
              openTo="month"
              format="MMMM YYYY"
              value={pickerDate(endMonth)}
              minDate={pickerDate(startMonth)}
              disabled={disabled}
              onChange={handleEndMonthChange}
              slotProps={{ textField: { fullWidth: !isTab, size: "small" } }}
            />
          </Stack>
        </LocalizationProvider>
      )}
    </Stack>
  );
}
